import React, { useEffect, useState } from 'react';
import axios from 'axios';

import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api/client';

interface FaqItem {
  id: number;
  question: string;
  answer: string;
  category: string;
  language: string;
  sortOrder: number;
}

export default function FaqPage() {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<FaqItem[]>([]);
  const [filtered, setFiltered] = useState<FaqItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_BASE}/faq`)
      .then(r => {
        setItems(r.data);
        setFiltered(r.data);
        const cats = Array.from(new Set<string>(r.data.map((f: FaqItem) => f.category).filter(Boolean)));
        setCategories(cats);
      })
      .finally(() => setLoading(false));
  }, [i18n.language]);

  useEffect(() => {
    let result = items;
    if (selectedCategory !== 'all') {
      result = result.filter(f => f.category === selectedCategory);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(f =>
        f.question.toLowerCase().includes(q) || f.answer.toLowerCase().includes(q)
      );
    }
    setFiltered(result);
    setOpenId(null);
  }, [search, selectedCategory, items]);

  return (
    <div style={s.page}>
      <h1 style={s.title}>{t('faq_title')}</h1>

      {/* Поиск */}
      <div style={s.searchBox}>
        <input
          style={s.searchInput}
          placeholder={`🔍 ${t('faq_search')}`}
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Категории */}
      {categories.length > 0 && (
        <div style={s.catBar}>
          <button
            style={{ ...s.catBtn, ...(selectedCategory === 'all' ? s.catActive : {}) }}
            onClick={() => setSelectedCategory('all')}
          >
            {t('docs_all')}
          </button>
          {categories.map(c => (
            <button
              key={c}
              style={{ ...s.catBtn, ...(selectedCategory === c ? s.catActive : {}) }}
              onClick={() => setSelectedCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* Список */}
      {loading ? (
        <div style={s.empty}>{t('faq_loading')}</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>{t('faq_not_found')}</div>
      ) : (
        <div style={s.list}>
          {filtered.map(f => (
            <div key={f.id} style={s.item}>
              <button
                style={{ ...s.question, ...(openId === f.id ? s.questionOpen : {}) }}
                onClick={() => setOpenId(openId === f.id ? null : f.id)}
              >
                <span>{f.question}</span>
                <span style={{ ...s.arrow, transform: openId === f.id ? 'rotate(180deg)' : 'none' }}>▼</span>
              </button>
              {openId === f.id && (
                <div style={s.answer}>{f.answer}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 800, margin: '0 auto', padding: '48px 40px' },
  title: { fontSize: '2rem', color: '#1a3a6b', marginBottom: 28 },
  langBar: { display: 'flex', gap: 8, marginBottom: 20 },
  langBtn: { padding: '7px 20px', borderRadius: 20, border: '2px solid #1a3a6b', background: 'transparent', color: '#1a3a6b', fontWeight: 600, cursor: 'pointer', fontSize: '0.88rem' },
  langActive: { background: '#1a3a6b', color: '#fff' },
  searchBox: { marginBottom: 20 },
  searchInput: { width: '100%', padding: '12px 16px', borderRadius: 10, border: '1px solid #dde3ef', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' },
  catBar: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 28 },
  catBtn: { padding: '6px 16px', borderRadius: 16, border: '1px solid #dde3ef', background: '#f8f9fa', color: '#555', cursor: 'pointer', fontSize: '0.85rem' },
  catActive: { background: '#1a3a6b', color: '#fff', border: '1px solid #1a3a6b' },
  empty: { textAlign: 'center', padding: 60, color: '#888' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  item: { border: '1px solid #e0e6f0', borderRadius: 10, overflow: 'hidden' },
  question: {
    width: '100%', background: '#f8f9fa', border: 'none',
    padding: '16px 20px', textAlign: 'left', cursor: 'pointer',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    fontSize: '0.98rem', fontWeight: 600, color: '#1a2a4a',
    transition: 'background 0.2s',
  },
  questionOpen: { background: '#1a3a6b', color: '#fff' },
  arrow: { fontSize: '0.75rem', transition: 'transform 0.3s', flexShrink: 0, marginLeft: 12 },
  answer: { padding: '16px 20px', background: '#fff', color: '#444', fontSize: '0.95rem', lineHeight: 1.8 },
};
